import { CanonicalSearchLoad, effectiveRate } from '@numeo/types'
import { useState } from 'react'
import { ArrowRight, Building2, ChevronDown, ChevronUp, Hash, Mail, MapPin, Package, Phone, Route, StickyNote, Truck, Weight } from 'lucide-react'
import { Badge } from 'components/ds'
import { formatMoney } from 'utils/formatters'

function rpm(load: CanonicalSearchLoad): string {
    const rate = effectiveRate(load)
    if (!rate || !load.distanceMiles) return ''
    return `$${(rate / load.distanceMiles).toFixed(2)}/mi`
}

function cityState(city?: string | null, st?: string | null): string {
    return [city, st].filter(Boolean).join(', ') || '—'
}

/**
 * Lean read-only load card for the Dispatch Help operator thread — mirrors the fields the mobile
 * driver sees (broker, rate, $/mi, route + deadheads, equipment) with an expand for broker contact.
 * A checkbox drives the multi-select the operator uses to act (book / ask info / bid).
 */
export function DispatchLoadCard({ load, selected, onToggle }: { load: CanonicalSearchLoad; selected: boolean; onToggle: (id: string) => void }) {
    const [open, setOpen] = useState(false)
    const rate = effectiveRate(load)
    return (
        <div
            className={`rounded-xl border px-3 py-2.5 text-sm shadow-sm transition-colors ${
                selected
                    ? 'border-blue-400 bg-blue-50 dark:border-blue-500/60 dark:bg-blue-500/10'
                    : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-slate-600'
            }`}
        >
            <div className="flex items-start gap-2.5">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggle(load.id)}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-blue-600"
                />
                <div className="min-w-0 flex-1">
                    {/* Broker + rate */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-100">
                            <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                            <span className="truncate">
                                #{load.rowNumber} · {load.brokerCompanyName || 'Broker'}
                            </span>
                        </div>
                        <div className="flex shrink-0 items-baseline gap-1">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatMoney(rate)}</span>
                            {rpm(load) && <span className="text-xs text-slate-400">{rpm(load)}</span>}
                        </div>
                    </div>
                    {/* Route */}
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                        <span className="truncate">
                            {cityState(load.pickupCity, load.pickupStateProv)}
                            {load.originDeadheadMiles != null && <span className="text-slate-400"> · DH {Math.round(load.originDeadheadMiles)}mi</span>}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600" />
                        <span className="truncate">{cityState(load.deliveryCity, load.deliveryStateProv)}</span>
                    </div>
                    {/* Spec badges */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {load.distanceMiles != null && (
                            <Badge variant="soft" type="default" size="sm" icon={<Route className="h-3 w-3" />}>
                                {Math.round(load.distanceMiles)} mi
                            </Badge>
                        )}
                        {load.weightLbs != null && (
                            <Badge variant="soft" type="default" size="sm" icon={<Weight className="h-3 w-3" />}>
                                {load.weightLbs.toLocaleString('en-US')} lbs
                            </Badge>
                        )}
                        {load.equipmentType && (
                            <Badge variant="soft" type="default" size="sm" icon={<Truck className="h-3 w-3" />}>
                                {load.equipmentType}
                            </Badge>
                        )}
                        {load.fullPartial && (
                            <Badge variant="soft" type="default" size="sm" icon={<Package className="h-3 w-3" />}>
                                {load.fullPartial}
                            </Badge>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="mt-1.5 flex items-center gap-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300"
                    >
                        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {open ? 'Less' : 'Details'}
                    </button>
                    {open && (
                        <div className="mt-1.5 space-y-1 border-t border-slate-100 pt-1.5 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                            {load.brokerContactEmail && (
                                <div className="flex items-center gap-1.5">
                                    <Mail className="h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500" />
                                    <span className="truncate">{load.brokerContactEmail}</span>
                                </div>
                            )}
                            {load.brokerContactPhone && (
                                <div className="flex items-center gap-1.5">
                                    <Phone className="h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500" />
                                    {load.brokerContactPhone}
                                </div>
                            )}
                            {load.brokerMc && (
                                <div className="flex items-center gap-1.5">
                                    <Hash className="h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500" />
                                    MC {load.brokerMc}
                                </div>
                            )}
                            {load.comment && (
                                <div className="flex items-start gap-1.5">
                                    <StickyNote className="mt-0.5 h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500" />
                                    <span className="whitespace-pre-wrap">{load.comment}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
